// Preview gating. Visiting `?preview=true` sets a persistent cookie; `?preview=false`
// clears it. The cookie state is surfaced to LaunchDarkly as a `preview` context
// attribute, so flags can target internal/preview sessions PRE-AUTH (no org or
// sign-in required — the CTA is evaluated for an anonymous visitor).
//
// NOTE: this is a preview convenience, NOT a security control. Anyone can set the
// cookie; it only reveals UI that is gated behind a LaunchDarkly flag. Never use it
// for authorisation.

const COOKIE = 'eh_preview';

// Pure: what the `?preview=` querystring is asking for.
export function readPreviewIntent(search: string): 'set' | 'clear' | null {
  const v = new URLSearchParams(search).get('preview');
  if (v === 'true') return 'set';
  if (v === 'false') return 'clear';
  return null;
}

// Pure: is the preview cookie present in a cookie string?
export function hasPreviewCookie(cookieString: string): boolean {
  return cookieString.split(';').some((c) => c.trim() === `${COOKIE}=1`);
}

// Pure: a cross-origin URL that keeps a preview session in preview. The cookie is per origin and
// LaunchDarkly evaluates per origin, so without this a preview tester's flags read off on the org host.
export function withPreview(url: string, cookieString: string): string {
  if (!hasPreviewCookie(cookieString)) return url;
  const target = new URL(url);
  target.searchParams.set('preview', 'true');
  return target.toString();
}

// Thin DOM glue (covered by E2E / manual): apply the `?preview=` intent to the
// cookie, strip the param from the URL, and return the resulting preview state for
// the LaunchDarkly context.
export function applyPreviewFromUrl(): boolean {
  const intent = readPreviewIntent(globalThis.location.search);
  if (intent === 'set') {
    document.cookie = `${COOKIE}=1; path=/; max-age=31536000; SameSite=Lax`;
  } else if (intent === 'clear') {
    document.cookie = `${COOKIE}=; path=/; max-age=0; SameSite=Lax`;
  }
  if (intent) {
    const url = new URL(globalThis.location.href);
    url.searchParams.delete('preview'); // keep other params (e.g. auth ?code=)
    globalThis.history.replaceState({}, '', url.toString());
  }
  return hasPreviewCookie(document.cookie);
}
